import type { AxiosRequestConfig, AxiosResponse } from "axios";

import { $streemApiV1 } from "shared/boot/api";
import {
  dateToTimestamp,
  getCurrentTimestamp,
  subtractTime,
} from "shared/helpers/date";
import type { StreamOptionsFilters } from "shared/helpers/StreamFilters";
import type {
  ChartLibraryProps,
  ChartLibraryRequestParams,
} from "shared/modules/Charts/types/data";
import type { SimpleBoolean } from "shared/resources/Stream";
import type { Nullable, Nullish, Stream } from "shared/types";
import type { Mention, MentionType } from "shared/types/mentions";

import ApiClient from "./ApiClient";

export interface HistogramBucket {
  2: {
    buckets: {
      doc_count: number;
      key: number;
      key_as_string: string;
    }[];
  };
  doc_count: number;
  key: string;
}

export type HistogramResponse = HistogramBucket[];

export interface AggregateBucket {
  doc_count: number;
  key: string;
}

export type AggregateResponse = AggregateBucket[];

export type InsightsResponse = ChartLibraryProps[];

export interface InsightsSummaryResponseStat {
  current: number | string;
  current_raw?: number | string;
  growth?: number;
  previous_raw?: number;
}

export interface InsightsSummaryResponse {
  audience: InsightsSummaryResponseStat;
  ave: InsightsSummaryResponseStat;
  mentions: InsightsSummaryResponseStat;
  sentiment: Omit<InsightsSummaryResponseStat, "current"> & { current: string };
}

export interface SocialInsightsSummaryResponse {
  engagement: InsightsSummaryResponseStat;
  posts: InsightsSummaryResponseStat;
  sentiment: InsightsSummaryResponseStat;
  top_emoji: InsightsSummaryResponseStat;
}

export interface CreateBookmarksParams {
  sourceStreamId?: number;
  targetId: number;
  targetType: string;
}

export interface CreateBookmarkResponse {
  count: number;
}

export interface ForceAddMentionsMentionData {
  id: number;
  type: MentionType;
}

export interface ForceAddMentionsParams {
  mentions: ForceAddMentionsMentionData[];
}

export interface SearchCoverageParams {
  after?: number;
  before?: number;
  collapseSyndicated?: boolean;
  excerptRadius?: number;
  includeExternalItems?: boolean;
  options?: Nullish<StreamOptionsFilters>;
  stream: Partial<Stream>;
  timeZone: Nullish<string>;
}

export interface CoverageSearchResponse {
  chart_data: Nullable<ChartLibraryProps>;
  mentions_data: Nullable<Mention[]>;
}

export interface ConvertSimpleBooleanQueryParams {
  simpleBoolean: SimpleBoolean;
}

export interface ConvertSimpleBooleanQueryResponse {
  boolean_query: string;
}

class StreamService extends ApiClient {
  constructor() {
    super("/streams");
  }

  aggregate(
    id: number,
    options = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<AggregateResponse>> {
    return this.get<AggregateResponse>(`/${id}/aggregate`, options);
  }

  convertSimpleBooleanQuery(
    params: ConvertSimpleBooleanQueryParams,
    options = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<ConvertSimpleBooleanQueryResponse>> {
    return $streemApiV1.post<ConvertSimpleBooleanQueryResponse>(
      "streams/convert_simple_boolean_query",
      {
        params: {
          simple_boolean_query: params.simpleBoolean,
        },
        ...options,
      }
    );
  }

  createBookmark(
    id: number,
    params: CreateBookmarksParams,
    options = <AxiosRequestConfig>{}
  ) {
    return this.post<CreateBookmarkResponse>(`/${id}/bookmarks`, {
      params: {
        source_stream: params.sourceStreamId,
        target_id: params.targetId,
        target_type: params.targetType,
      },
      ...options,
    });
  }

  forceAddMentions(
    id: number,
    params: ForceAddMentionsParams,
    options = <AxiosRequestConfig>{}
  ) {
    return this.post<void>(`/${id}/force_add_mentions`, {
      params: { mentions: params.mentions },
      ...options,
    });
  }

  histogram(
    id: number,
    options = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<HistogramResponse>> {
    return this.get<HistogramResponse>(`/${id}/histogram`, options);
  }

  insights(
    id?: number,
    options = <AxiosRequestConfig<ChartLibraryRequestParams>>{}
  ): Promise<AxiosResponse<InsightsResponse>> {
    let path = "/insights";

    if (id) {
      path = `/${id}/insights`;
    }

    return this.post<InsightsResponse>(path, options);
  }

  insightsSummary(
    id: number,
    options = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<InsightsSummaryResponse>> {
    return this.post<InsightsSummaryResponse>(
      `/${id}/insights_summary`,
      options
    );
  }

  searchCoverage(
    params: SearchCoverageParams,
    requestOptions = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<CoverageSearchResponse>> {
    return $streemApiV1.post<CoverageSearchResponse>("coverage/search", {
      params: {
        after:
          params.after ?? dateToTimestamp(subtractTime(new Date(), 30, "day")),
        before: params.before ?? getCurrentTimestamp(),
        collapse_syndicated: params.collapseSyndicated ?? true,
        excerpt_radius: params.excerptRadius ?? 60,
        include_external_items: params.includeExternalItems ?? false,
        ...(params.options ?? {}),
        stream: params.stream,
        time_zone: params.timeZone,
      },
      ...requestOptions,
    });
  }

  socialInsightsSummary(
    id: number,
    options = <AxiosRequestConfig>{}
  ): Promise<AxiosResponse<SocialInsightsSummaryResponse>> {
    return this.post<SocialInsightsSummaryResponse>(
      `/${id}/social_insights_summary`,
      options
    );
  }
}

export default new StreamService();
